<template>
    <header
        id="navigation"
        class="bg-[#fafafa] z-50 pt-[25px] pb-[18px] fixed top-0 right-0 left-0"
    >
        <div class="flex items-center justify-between mx-auto max-w-[1400px]">
            <router-link id="logo-link" :to="timesheetNavigationTarget">
                <img
                    src="@/assets/logo/nanoo_tv_Logo.svg"
                    alt="nanoo.tv"
                    id="logo"
                    class="w-[190px] lg:w-[210px] pl-5"
                />
            </router-link>
            <nav>
                <ul class="flex pr-5" id="user-dropdown">
                    <v-menu open-on-hover v-for="(elem, index) in navbarElements" :key="index">
                        <template v-slot:activator="{ props }" :id="elem.title">
                            <h2 v-bind="props" class="pl-[20px]">
                                <router-link :to="elem.link ? elem.link : ''">
                                    {{ elem.title }}
                                    <i
                                        class="bi-chevron-down ml-[5px] text-[#db0720]"
                                        v-if="elem.subpoints"
                                    ></i
                                ></router-link>
                            </h2>
                        </template>
                        <v-list v-if="elem.subpoints" id="subpointsList">
                            <v-list-item
                                v-for="(item, index) in elem.subpoints"
                                :key="index"
                                @click="item.method"
                            >
                                <v-list-item-title>
                                    <a
                                        :href="item.link"
                                        class="pt-[10px] pr-[20px] pb-[10px] pl-[10px]"
                                    >
                                        <i class="bi-dash ml-[5px] pr-[10px] text-[#db0720]"></i>
                                        {{ item.title }}</a
                                    ></v-list-item-title
                                >
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-menu open-on-hover id="language">
                        <template v-slot:activator="{ props }">
                            <h2
                                v-bind="props"
                                class="pl-[20px] cursor-pointer"
                                @click="
                                    $i18n.locale == 'en-GB'
                                        ? ($i18n.locale = 'de-CH')
                                        : ($i18n.locale = 'en-GB')
                                "
                            >
                                {{ $i18n.locale == 'en-GB' ? 'EN' : 'DE' }}
                                <i class="bi-arrow-repeat ml-[5px] text-[#db0720]"></i>
                            </h2>
                        </template>
                    </v-menu>
                    <li class="relative group pl-[20px]" :title="'Logged in as: ' + 'placeholder'">
                        <button @click="logout()" id="logout">Logout</button>
                    </li>
                </ul>
            </nav>
        </div>
    </header>
</template>

<script lang="ts" setup>
    import { ref, computed, onMounted } from 'vue'
    import { usePermissionsStore } from '@/stores/PermissionsStore'
    import useContactStore from '@/utils/useContactStore'
    import fetchData from '@/model/Storage'
    import { useRoute } from 'vue-router'

    const permissionsStore = usePermissionsStore()
    const { selectedContact } = useContactStore()
    const route = useRoute()

    const hasTimesheetPermission = computed(() => {
        return permissionsStore.links.some((link) => link.href === '/timesheet')
    })

    const timesheetNavigationTarget = computed(() => {
        if (hasTimesheetPermission.value) {
            return '/timesheet'
        } else {
            // Defensively check for the existence of route and route.path
            const basePath = route && route.path ? '/' + route.path.split('/')[1] : '/'
            return basePath
        }
    })

    // Assume predefinedElements now only contains the top-level menu items without subpoints
    const predefinedElements = ref([
        { title: 'Timesheet', link: '/timesheet', subpoints: null },
        {
            title: 'Links',
            link: null,
            subpoints: [
                { title: 'Sharepoint', link: 'https://nanoo.sharepoint.com/' },
                { title: 'Dynamics', link: 'https://werft22crm.crm4.dynamics.com/' },
                { title: 'FileMaker', link: 'fmp://fms-2016.werft22.net/WerftZentrale' },
            ],
        },
        {
            title: 'Finanzdienste',
            link: null,
            subpoints: [{ title: 'VISA', link: '/visa' }],
        },
        {
            title: 'Sales',
            link: null,
            subpoints: [
                { title: 'Offertentool', link: '/offers' },
                { title: 'Adressbuch', link: '/contacts', method: clearContactForm },
            ],
        },
        {
            title: 'Extern',
            link: null,
            subpoints: [
                { title: 'TIPTEL', link: '/tiptel' },
                { title: 'Admin', link: '/admin' },
                { title: 'Project NICE', link: '/nice' },
            ],
        },
        {
            title: 'Media',
            link: '/media',
            subpoints: [{ title: 'Video Player', link: '/media/player' }],
        },
    ])

    // Fetch user links on component mount
    // this has to be done in the setup function
    // because the navigation gets loaded as one of the first components
    onMounted(() => {
        permissionsStore.fetchUserLinks()
        console.log(permissionsStore.links)
    })

    // Computed property to get the navbar elements based on user permissions
    const navbarElements = computed(() => {
        const userLinkHrefs = permissionsStore.links.map((link) => link.href)
        return predefinedElements.value.reduce((acc, elem) => {
            if (!elem.subpoints) {
                // Directly include top-level menu items if the user has the permission
                if (userLinkHrefs.includes(elem.link)) {
                    acc.push(elem)
                }
            } else {
                // Filter subpoints based on user permissions
                const permittedSubpoints = elem.subpoints.filter((subpoint) =>
                    userLinkHrefs.includes(subpoint.link)
                )

                // Only include top-level menu items if there are permitted subpoints
                if (permittedSubpoints.length > 0) {
                    acc.push({ ...elem, subpoints: permittedSubpoints })
                }
            }
            return acc
        }, [])
    })

    function clearContactForm() {
        selectedContact.value = {}
        selectedContact.value.person = {}
        selectedContact.value.organisation = {}
        selectedContact.value.billing = {}
    }

    async function logout() {
        console.log('logout')
        await fetchData({ method: 'DELETE', endpoint: 'logout' })
    }
</script>
